import { prisma, reportingPrisma, ParticipantGroupType } from "client";
import { expandCaseInsensitiveMatches } from "./filter-matches.pure";
import type { ProfileFieldEquals } from "./everyone-pagination.pure";

async function expandIndexedProfileValues(
  eventId: string,
  field: "country" | "company" | "jobTitle",
  requested: string,
  client: typeof prisma = prisma
): Promise<string[]> {
  const rows = await client.profile.findMany({
    where: { eventId, [field]: { not: null } },
    distinct: [field],
    select: { country: true, company: true, jobTitle: true },
  });
  return expandCaseInsensitiveMatches(
    rows.map((row) => row[field]),
    [requested]
  );
}

async function storedCompanyTagNames(eventId: string): Promise<string[]> {
  const [exhibitorRows, sponsorRows] = await Promise.all([
    prisma.exhibitorTranslations.findMany({
      where: {
        exhibitor: {
          eventId,
          participantGroupId: { not: null },
        },
      },
      select: { tags: true },
    }),
    prisma.sponsorTranslations.findMany({
      where: {
        sponsor: { eventId },
      },
      select: { tags: true },
    }),
  ]);

  const names = new Set<string>();
  for (const row of [...exhibitorRows, ...sponsorRows]) {
    for (const tag of row.tags) {
      if (tag?.trim()) names.add(tag);
    }
  }
  return [...names];
}

/**
 * Map request filter strings to stored equality values for the reporting
 * indexes on Profile(eventId, country|company|jobTitle). Returns `null` when
 * any provided filter has no case-insensitive match (empty page).
 */
export async function resolveProfileFilterEquals(
  eventId: string,
  filters: { country?: string; company?: string; jobTitle?: string }
): Promise<ProfileFieldEquals | null> {
  const equals: ProfileFieldEquals = {};

  if (filters.country) {
    const matches = await expandIndexedProfileValues(
      eventId,
      "country",
      filters.country,
      reportingPrisma
    );
    if (!matches.length) return null;
    equals.country = matches;
  }

  if (filters.company) {
    const matches = await expandIndexedProfileValues(
      eventId,
      "company",
      filters.company,
      reportingPrisma
    );
    if (!matches.length) return null;
    equals.company = matches;
  }

  if (filters.jobTitle) {
    const matches = await expandIndexedProfileValues(
      eventId,
      "jobTitle",
      filters.jobTitle,
      reportingPrisma
    );
    if (!matches.length) return null;
    equals.jobTitle = matches;
  }

  return equals;
}

export async function resolveProfileFilterIds(
  eventId: string,
  filters: { country?: string; company?: string; jobTitle?: string }
) {
  const where: {
    eventId: string;
    active: { not: false };
    country?: { in: string[] };
    company?: { in: string[] };
    jobTitle?: { in: string[] };
  } = {
    eventId,
    active: { not: false },
  };

  if (filters.country) {
    const matches = await expandIndexedProfileValues(
      eventId,
      "country",
      filters.country
    );
    if (!matches.length) {
      return { profileIds: [] as string[], userIds: [] as string[] };
    }
    where.country = { in: matches };
  }

  if (filters.company) {
    const matches = await expandIndexedProfileValues(
      eventId,
      "company",
      filters.company
    );
    if (!matches.length) {
      return { profileIds: [] as string[], userIds: [] as string[] };
    }
    where.company = { in: matches };
  }

  if (filters.jobTitle) {
    const matches = await expandIndexedProfileValues(
      eventId,
      "jobTitle",
      filters.jobTitle
    );
    if (!matches.length) {
      return { profileIds: [] as string[], userIds: [] as string[] };
    }
    where.jobTitle = { in: matches };
  }

  const profiles = await prisma.profile.findMany({
    where,
    select: { id: true, userId: true },
  });

  return {
    profileIds: profiles.map((profile) => profile.id),
    userIds: [
      ...new Set(
        profiles
          .map((profile) => profile.userId)
          .filter((id): id is string => Boolean(id))
      ),
    ],
  };
}

export async function narrowCompaniesByTeamUserIds(
  eventId: string,
  userIds: string[],
  ids: { exhibitorIds: string[]; sponsorIds: string[] }
) {
  if (!userIds.length) {
    return { exhibitorIds: [] as string[], sponsorIds: [] as string[] };
  }

  const [exhibitors, sponsors] = await Promise.all([
    ids.exhibitorIds.length
      ? prisma.exhibitor.findMany({
          where: {
            eventId,
            id: { in: ids.exhibitorIds },
            teamIds: { hasSome: userIds },
          },
          select: { id: true },
        })
      : Promise.resolve([]),
    ids.sponsorIds.length
      ? prisma.sponsor.findMany({
          where: {
            eventId,
            id: { in: ids.sponsorIds },
            teamIds: { hasSome: userIds },
          },
          select: { id: true },
        })
      : Promise.resolve([]),
  ]);

  return {
    exhibitorIds: exhibitors.map((row) => row.id),
    sponsorIds: sponsors.map((row) => row.id),
  };
}

export async function resolveCompaniesByTeamUserIds(
  eventId: string,
  userIds: string[],
  type?: ParticipantGroupType
) {
  if (!userIds.length) {
    return { exhibitorIds: [] as string[], sponsorIds: [] as string[] };
  }

  const includeExhibitors = !type || type === ParticipantGroupType.EXHIBITORS;
  const includeSponsors = !type || type === ParticipantGroupType.SPONSORS;

  const [exhibitors, sponsors] = await Promise.all([
    includeExhibitors
      ? prisma.exhibitor.findMany({
          where: { eventId, teamIds: { hasSome: userIds } },
          select: { id: true },
        })
      : Promise.resolve([]),
    includeSponsors
      ? prisma.sponsor.findMany({
          where: { eventId, teamIds: { hasSome: userIds } },
          select: { id: true },
        })
      : Promise.resolve([]),
  ]);

  return {
    exhibitorIds: exhibitors.map((row) => row.id),
    sponsorIds: sponsors.map((row) => row.id),
  };
}

export async function resolveExhibitorSponsorTagIds(
  eventId: string,
  tagNames: string[],
  type?: ParticipantGroupType
) {
  const storedTags = expandCaseInsensitiveMatches(
    await storedCompanyTagNames(eventId),
    tagNames
  );
  if (!storedTags.length) {
    return { exhibitorIds: [] as string[], sponsorIds: [] as string[] };
  }

  const includeExhibitors = !type || type === ParticipantGroupType.EXHIBITORS;
  const includeSponsors = !type || type === ParticipantGroupType.SPONSORS;

  const [exhibitorRows, sponsorRows] = await Promise.all([
    includeExhibitors
      ? prisma.exhibitorTranslations.findMany({
          where: { tags: { hasSome: storedTags } },
          select: { exhibitorId: true },
        })
      : Promise.resolve([] as Array<{ exhibitorId: string }>),
    includeSponsors
      ? prisma.sponsorTranslations.findMany({
          where: { tags: { hasSome: storedTags } },
          select: { sponsorId: true },
        })
      : Promise.resolve([] as Array<{ sponsorId: string }>),
  ]);

  const exhibitorCandidateIds = [
    ...new Set(exhibitorRows.map((row) => row.exhibitorId)),
  ];
  const sponsorCandidateIds = [
    ...new Set(sponsorRows.map((row) => row.sponsorId)),
  ];

  const [exhibitors, sponsors] = await Promise.all([
    exhibitorCandidateIds.length
      ? prisma.exhibitor.findMany({
          where: { eventId, id: { in: exhibitorCandidateIds } },
          select: { id: true },
        })
      : Promise.resolve([]),
    sponsorCandidateIds.length
      ? prisma.sponsor.findMany({
          where: { eventId, id: { in: sponsorCandidateIds } },
          select: { id: true },
        })
      : Promise.resolve([]),
  ]);

  return {
    exhibitorIds: exhibitors.map((row) => row.id),
    sponsorIds: sponsors.map((row) => row.id),
  };
}
